import { useRef, useCallback, useEffect } from "react";
import * as THREE from "three";

/**
 * Tap-to-Teleport hook.
 * Owns:
 *   - Cyan preview ring (`indicatorRef`) shown during pointer-down.
 *   - Pointer event handlers on the renderer's DOM element.
 *   - Raycaster against [houseGroup, envGroup].
 *   - The active teleport state (`teleportRef`) — { tx, tz, ty, lookY,
 *     camOffset, startTime, durationMs, startCamPos, startTarget }.
 *
 * The hook does NOT animate inside its own loop — instead, the parent's tick
 * loop calls `runTeleportFrame()` every frame. This keeps a single source of
 * truth for the animation cadence and gives `useCinematicTour` a callback
 * hook when a waypoint completes.
 *
 * Usage (inside the renderer-setup useEffect):
 *   useTapToTeleport.attachToScene({ scene, renderer, camera, controls,
 *     houseGroupRef, envGroupRef, walkthroughFloorYRef });
 *
 *   // In tick:
 *   const finished = runTeleportFrame();
 *   if (finished) onTeleportComplete?.();
 *
 * @returns {object}
 *   { teleportRef, indicatorRef, beginTeleport, attachToScene,
 *     detachFromScene, runTeleportFrame, setOnTeleportComplete }
 */
export default function useTapToTeleport({ walkthroughFloorYRef }) {
  const teleportRef = useRef(null);
  const indicatorRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const pointerDownRef = useRef(null);
  const onCompleteRef = useRef(null);
  // Refs set by attachToScene so handlers can read current scene state.
  const sceneCtxRef = useRef(null);

  const beginTeleport = useCallback((wp) => {
    const ctx = sceneCtxRef.current;
    if (!ctx) return;
    const { camera, controls } = ctx;
    if (!camera || !controls) return;
    teleportRef.current = {
      tx: wp.x,
      tz: wp.z,
      ty: wp.eyeY ?? walkthroughFloorYRef.current,
      lookY: wp.lookY ?? 1.0,
      camOffset: wp.camOffset ?? 6,
      startTime: performance.now(),
      durationMs: wp.durationMs ?? 1400,
      startCamPos: camera.position.clone(),
      startTarget: controls.target.clone(),
    };
  }, [walkthroughFloorYRef]);

  const setOnTeleportComplete = useCallback((cb) => {
    onCompleteRef.current = cb;
  }, []);

  const runTeleportFrame = useCallback(() => {
    const tp = teleportRef.current;
    const ctx = sceneCtxRef.current;
    if (!tp || !ctx) return false;
    const { camera, controls } = ctx;
    const t = (performance.now() - tp.startTime) / tp.durationMs;
    const e = t >= 1 ? 1 : 1 - Math.pow(1 - t, 3);
    const destCam = new THREE.Vector3(tp.tx, tp.ty + 0.2, tp.tz);
    const offset = tp.startCamPos.clone()
      .sub(tp.startTarget).normalize().multiplyScalar(tp.camOffset || 4);
    destCam.add(offset);
    camera.position.lerpVectors(tp.startCamPos, destCam, e);
    controls.target.lerpVectors(
      tp.startTarget,
      new THREE.Vector3(tp.tx, tp.lookY + 1.0, tp.tz),
      e,
    );
    controls.update();
    if (t >= 1) {
      teleportRef.current = null;
      onCompleteRef.current?.();
      return true;
    }
    return false;
  }, []);

  const attachToScene = useCallback(({ scene, renderer, camera, controls, houseGroupRef, envGroupRef }) => {
    // Build the cyan preview ring once and add to the scene.
    if (!indicatorRef.current) {
      const ringGeo = new THREE.RingGeometry(0.8, 1.0, 48);
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0x06b6d4, transparent: true, opacity: 0.85,
        side: THREE.DoubleSide, depthTest: false,
      });
      const indicator = new THREE.Mesh(ringGeo, ringMat);
      indicator.rotation.x = -Math.PI / 2;
      indicator.visible = false;
      indicator.renderOrder = 999;
      scene.add(indicator);
      indicatorRef.current = indicator;
    }

    const raycastFloor = (clientX, clientY) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((clientY - rect.top) / rect.height) * 2 + 1;
      const ray = raycasterRef.current;
      ray.setFromCamera(new THREE.Vector2(nx, ny), camera);
      const targets = [houseGroupRef.current, envGroupRef.current].filter(Boolean);
      const intersects = ray.intersectObjects(targets, true);
      return intersects[0] || null;
    };

    const onPointerDown = (e) => {
      pointerDownRef.current = { x: e.clientX, y: e.clientY, t: performance.now() };
      const hit = raycastFloor(e.clientX, e.clientY);
      if (hit && indicatorRef.current) {
        indicatorRef.current.position.set(hit.point.x, hit.point.y + 0.02, hit.point.z);
        indicatorRef.current.visible = true;
      }
    };
    const onPointerUp = (e) => {
      const pd = pointerDownRef.current;
      pointerDownRef.current = null;
      if (indicatorRef.current) indicatorRef.current.visible = false;
      if (!pd) return;
      const dx = e.clientX - pd.x;
      const dy = e.clientY - pd.y;
      const distPx = Math.sqrt(dx * dx + dy * dy);
      const dtMs = performance.now() - pd.t;
      if (distPx > 6 || dtMs > 400) return;
      const hit = raycastFloor(e.clientX, e.clientY);
      if (!hit) return;
      teleportRef.current = {
        tx: hit.point.x,
        tz: hit.point.z,
        ty: walkthroughFloorYRef.current,
        lookY: Math.max(0, hit.point.y),
        camOffset: 4,
        startTime: performance.now(),
        durationMs: 900,
        startCamPos: camera.position.clone(),
        startTarget: controls.target.clone(),
      };
    };
    const onPointerCancel = () => {
      pointerDownRef.current = null;
      if (indicatorRef.current) indicatorRef.current.visible = false;
    };

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("pointercancel", onPointerCancel);
    renderer.domElement.addEventListener("pointerleave", onPointerCancel);

    sceneCtxRef.current = {
      scene, renderer, camera, controls, houseGroupRef, envGroupRef,
      handlers: { onPointerDown, onPointerUp, onPointerCancel },
    };
  }, [walkthroughFloorYRef]);

  const detachFromScene = useCallback(() => {
    const ctx = sceneCtxRef.current;
    if (!ctx) return;
    const { renderer, handlers } = ctx;
    renderer.domElement.removeEventListener("pointerdown", handlers.onPointerDown);
    renderer.domElement.removeEventListener("pointerup", handlers.onPointerUp);
    renderer.domElement.removeEventListener("pointercancel", handlers.onPointerCancel);
    renderer.domElement.removeEventListener("pointerleave", handlers.onPointerCancel);
    sceneCtxRef.current = null;
  }, []);

  // Auto-detach on unmount as a safety net (parent should call detachFromScene
  // explicitly during its renderer cleanup).
  useEffect(() => () => detachFromScene(), [detachFromScene]);

  return {
    teleportRef,
    indicatorRef,
    beginTeleport,
    attachToScene,
    detachFromScene,
    runTeleportFrame,
    setOnTeleportComplete,
  };
}
